import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { formatCurrency, formatDurationShort } from '../../utils/format';
import endpoints from '../../define/endpoints';
import { TRANSLATIONS } from '../../../translations';
import { BADGE_TYPES } from '../../../constants';

import AddToWishList from '../AddToWishList';
import { Icon } from '@iconify/react';
import iconMapMarker from '@iconify-icons/mdi/map-marker';
import iconClock from '@iconify-icons/mdi/clock-outline';
import iconStar from '@iconify-icons/mdi/star';
import iconPeople from '@iconify-icons/mdi/people';
import iconLightning from '@iconify/icons-flat-color-icons/flash-on';

// TODO: integrate card description from core api
// NOTE: markup should match `card.twig` view
const ListingCard = ({
  city,
  distanceKm,
  durationMinAggr,
  id,
  priceMinAggr,
  priceType,
  priceLabel,
  seatsMinAggr,
  seatsMaxAggr,
  province,
  country,
  reviewScoreAverage,
  slug,
  summary,
  coverImage,
  coverImagePrecrop,
  title,
  available,
  className,
  cardsCompact,
  flippedProps,
  badges,
  position,
  distanceFrom,
  listType,
  renderImmediateConfirmationBadge,
  renderNightStayBadge = false,
  categories,
}) => {
  // Derive macro_category (first) and category (second) from the categories array
  const macroCategory = Array.isArray(categories) && categories[0] ? (categories[0].title || null) : null;
  const category = Array.isArray(categories) && categories[1] ? (categories[1].title || null) : null;
  const COUNTRY_ARRAY = ['Italy', 'Italia', 'Spain', 'España', 'Spagna'];
  const countLines = (ref) => {
    const rects = ref.current?.getClientRects();
    if (!rects) {
      return 1;
    }
    let lines = 0;
    for (let i = 0; i < rects.length; i++) {
      if (rects[i - 1]) {
        if (rects[i - 1].y !== rects[i].y) {
          lines++;
        }
      } else {
        lines++;
      }
    }
    return lines;
  };

  const titleLink = useRef();
  const [titleLines, setTitleLines] = useState();
  useEffect(() => {
    setTitleLines(countLines(titleLink));
  }, []);

  const participantsLabelRef = useRef();
  const [participantsLabel, setParticipantsLabel] = useState('');

  useEffect(() => {
    let newParticipantsLabel = '';
    let participantsLabelLines = countLines(participantsLabelRef)
    const shortened = 'pax';
    const singular = participantsLabelLines == 1 ? TRANSLATIONS.PARTICIPANT : shortened;
    const plural = participantsLabelLines == 1 ? TRANSLATIONS.PARTICIPANTS : shortened;
    if ((seatsMinAggr || seatsMaxAggr) && !participantsLabel.includes(shortened)) {
      if (seatsMinAggr == seatsMaxAggr) {
        if (seatsMinAggr == 1) {
          newParticipantsLabel = `${seatsMinAggr} ${singular}`;
        } else {
          newParticipantsLabel = `${seatsMinAggr} ${plural}`;
        }
      } else if (seatsMinAggr && !seatsMaxAggr) {
        newParticipantsLabel = `${seatsMinAggr} ${plural}`;
      } else if (!seatsMinAggr && seatsMaxAggr) {
        newParticipantsLabel = `${seatsMaxAggr} ${plural}`;
      } else {
        newParticipantsLabel = `${seatsMinAggr}-${seatsMaxAggr} ${plural}`;
      }
      setParticipantsLabel(newParticipantsLabel);
    }
  }, [participantsLabel]);

  const immediateConfirmationBadge = (
    <div className="card__badge">
      <span className="text-has-icon">
        <Icon
          icon={iconLightning}
          className="icon"
          style={{ transform: 'scale(0.75)' }}
        />
        {TRANSLATIONS.IMMEDIATE_CONFIRMATION}
      </span>
    </div>
  );

  const shouldBadgeRender = !!renderImmediateConfirmationBadge && titleLines < 3;

  return (
    <article
      className={cn(
        'card',
        {
          'card--compact': cardsCompact,
          'card--unavailable': !available,
        },
        className,
        { 'card--with-night-stay': renderNightStayBadge }
      )}
      {...flippedProps}
      data-item_name={title}
      data-item_id={id}
      data-item_price={priceMinAggr}
      data-item_index={position + 1}
    >
      {/* eslint-disable-next-line react/jsx-no-target-blank */}
      <a
        href={`${TRANSLATIONS.PRODUCT_DIRECTORY}/${slug}/`}
        onClick={() => {
          dataLayer.push({
            event: 'productClick',
            ecommerce: {
              click: {
                actionField: { list: listType },
                products: [
                  {
                    id: id,
                    position: position + 1,
                  },
                ],
              },
            },
          });
        }}
        target={'_blank'}
        rel={'noopener'}
        data-gtm-item="product-image"
        style={{ position: 'relative' }}
      >
        <figure className="card__image">
          <img
            srcSet={`
            ${endpoints.bucket}${coverImage}?width=278&height=185&fit=crop${coverImagePrecrop ? `&precrop=${coverImagePrecrop}` : ''} 1x,
            ${endpoints.bucket}${coverImage}?width=556&height=370&fit=crop${coverImagePrecrop ? `&precrop=${coverImagePrecrop}` : ''} 2x,
          `}
            alt={title}
            loading="lazy"
          />
        </figure>
        {renderNightStayBadge && !cardsCompact && available && <div className={cn(
          'card__badge card__badge_night card__badge_night--red',
        )}>
          <span className="text-has-icon">
            <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.75 6.9395L6.71933 0.9695C7.01267 0.676833 7.48733 0.676833 7.78 0.9695L13.75 6.9395M2.25 5.4395V12.1895C2.25 12.6035 2.586 12.9395 3 12.9395H5.75V9.6895C5.75 9.2755 6.086 8.9395 6.5 8.9395H8C8.414 8.9395 8.75 9.2755 8.75 9.6895V12.9395H11.5C11.914 12.9395 12.25 12.6035 12.25 12.1895V5.4395M4.75 12.9395H10.25" stroke="#5C6797" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {TRANSLATIONS.INCLUDED_NIGHT}
          </span>
        </div>}
      </a>

      <div className="card__inner">
        <div className="card__inner__top">
          <div className="card__inner__top__content">
            {(city || province) && (
              <div className="card__address">
                <span className="text-has-icon">
                  <Icon icon={iconMapMarker} className="icon" />
                  {distanceFrom && Math.round(distanceKm) >= 1 && (
                    <span className="ellipsis">
                      {city} •{' '}
                      <strong>
                        {Math.round(distanceKm)} km {TRANSLATIONS.PRICEFROM}{' '}
                        {distanceFrom}
                      </strong>{' '}
                    </span>
                  )}
                  {(!distanceFrom || Math.round(distanceKm) < 1) && (
                    <span className="ellipsis">
                      {city}
                      {province && province !== city && (', ' + province)}
                      {country && !COUNTRY_ARRAY.includes(country.trim()) && (', ' + country.trim())}
                    </span>
                  )}
                </span>
              </div>
            )}
            {title && (
              <h3 className="card__title">
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a
                  href={`${TRANSLATIONS.PRODUCT_DIRECTORY}/${slug}/`}
                  onClick={() => {
                    dataLayer.push({
                      event: 'productClick',
                      ecommerce: {
                        click: {
                          actionField: { list: listType },
                          products: [
                            {
                              id: id,
                              position: position + 1,
                            },
                          ],
                        },
                      },
                    });
                  }}
                  ref={titleLink}
                  target={'_blank'}
                  rel={'noopener'}
                >
                  {title}
                </a>
              </h3>
            )}
            {cardsCompact && !!summary && (
              <p className="card__description">{summary}</p>
            )}
          </div>
          <div className="card__controls">
            <AddToWishList listingId={id} activityName={title} category={category} macroCategory={macroCategory} />
          </div>
          {shouldBadgeRender === true && (
            <div className="card__badges">{immediateConfirmationBadge}</div>
          )}
        </div>
        <div className="card__inner__bottom">
          {!available && (
            <div className="card__badges card__badges--sticky">
              <span className="badge badge--secondary">
                {TRANSLATIONS.NOT_AVAILABLE}
              </span>
            </div>
          )}

          {!!badges && !!badges.length && (
            <div className="card__badges card__badges--sticky">
              {badges.map((badge, i) => {
                return (
                  <span
                    key={i}
                    className={cn('badge', {
                      'badge--success': badge.type === BADGE_TYPES.SUCCESS,
                      'badge--warning': badge.type === BADGE_TYPES.DANGER,
                      'badge--alert': badge.type === BADGE_TYPES.WARNING,
                    })}
                  >
                    {badge.label}
                  </span>
                );
              })}
            </div>
          )}
          {(!!durationMinAggr || !!reviewScoreAverage) && (
            <div className="card__inner__bottom__details">
              <div className="card__row">
                {durationMinAggr && (
                  <span className="text-has-icon card__duration">
                    <Icon icon={iconClock} className="icon" />
                    {formatDurationShort(durationMinAggr, renderNightStayBadge)}
                  </span>
                )}
                {reviewScoreAverage && (
                  <span className="text-has-icon card__rating">
                    <Icon icon={iconStar} className="icon" />{' '}
                    <span className="card__rating__text">
                      {`${(Math.round(reviewScoreAverage * 10) / 10).toFixed(
                        1
                      )}`}
                    </span>
                  </span>
                )}
              </div>
              <div className="card__row">
                {participantsLabel && (
                  <span className="text-has-icon card__participants">
                    <Icon icon={iconPeople} className="icon" />
                    <span style={{ display: 'inline' }}>
                      <span ref={participantsLabelRef}>
                        {participantsLabel}
                      </span>
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="card__inner__bottom__price">
            {priceMinAggr && (
              <div className="card__price__amount card__row">
                <span>
                  {TRANSLATIONS.PRICEFROM}{' '}
                  <strong>{formatCurrency(priceMinAggr, true, true)}</strong>
                </span>
              </div>
            )}
            {priceLabel && (
              <div className="card__price__type card__row">
                <span>{priceLabel}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

ListingCard.defaultProps = {
  city: null,
  distanceKm: null,
  description: null,
  durationMinAggr: 0,
  id: null,
  priceMinAggr: 0,
  province: null,
  reviewScoreAverage: 0,
  slug: null,
  coverImage: null,
  coverImagePrecrop: null,
  title: null,
  flippedProps: {},
  cardsCompact: false,
  position: 0,
  distanceFrom: '',
  listType: '',
  categories: null,
};

ListingCard.propTypes = {
  className: PropTypes.string,
  cardsCompact: PropTypes.bool,
  city: PropTypes.string,
  distanceKm: PropTypes.number,
  description: PropTypes.string,
  durationMinAggr: PropTypes.number,
  id: PropTypes.number,
  position: PropTypes.number,
  distanceFrom: PropTypes.string,
  listType: PropTypes.string,
  priceMinAggr: PropTypes.number,
  province: PropTypes.string,
  reviewScoreAverage: PropTypes.number,
  slug: PropTypes.string,
  summary: PropTypes.string,
  coverImage: PropTypes.string,
  coverImagePrecrop: PropTypes.string,
  title: PropTypes.string,
  flippedProps: PropTypes.object,
  categories: PropTypes.array,
  badges: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf([
        BADGE_TYPES.SUCCESS,
        BADGE_TYPES.WARNING,
        BADGE_TYPES.DANGER,
      ]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

export default ListingCard;
